import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { StatCard } from "@outpave/ds";
import { formatCashFull } from "utils/formatCash";
import { ComplianceMetrics } from "hooks/useByocDashboard";

export interface UnallocatedSpendCardProps {
  metrics: ComplianceMetrics;
  loading?: boolean;
  /** The query failed with nothing cached, so `metrics` is the empty fallback
   *  rather than a measurement. Without this the card reads a failure as
   *  everything being allocated. */
  error?: boolean;
}

/**
 * Spend that has landed but has not been assigned to an allocation. Leads with
 * the dollar figure rather than the count, since the money is what an admin is
 * chasing here. Opens the Card Expenses tasks view.
 */
const UnallocatedSpendCard: React.FC<UnallocatedSpendCardProps> = ({
  metrics,
  loading = false,
  error = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const currency = metrics.currency ?? "usd";
  const hasBacklog = metrics.count > 0;

  return (
    <StatCard
      title={t("Unallocated Spend")}
      value={error ? "-" : formatCashFull(metrics.totalAmount, currency, false)}
      loading={loading}
      caption={
        error ? (
          t("Something went wrong")
        ) : hasBacklog ? (
          <>
            <Box
              component="span"
              sx={{ color: "var(--color-text-danger)", marginRight: "6px" }}
            >
              {t("{{n}} transactions", { n: metrics.count })}
            </Box>
            {t("no allocation set")}
          </>
        ) : (
          t("Everything is allocated")
        )
      }
      action={{ type: "open", onClick: () => navigate("/admin/transactions") }}
    />
  );
};

export default UnallocatedSpendCard;
